import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { apiRoutesUrls } from "Api/ApiNames";
import { toUrlSlug } from "../../Components/Helpers/toUrlSlug";
import listStyles from "../ArchiveList/ArchiveList.module.css";
import translation from "../../Components/Helpers/translation";
import { LoaderPage } from "../../Components/LoaderPage/loaderPage";
import Footer from "../Footer/Footer";
import Loader from "../../Components/Loader/Loader";

const PAGE_SIZE = 20;

function formatAuthors(authors) {
    if (!authors || !authors.length) {
        return "";
    }
    return authors
        .map((a) => [a.first_name, a.last_name].filter(Boolean).join(" "))
        .filter(Boolean)
        .join(", ");
}

const Articles = () => {
    const { lang } = useParams();
    const [data, setData] = useState(null);
    const [offset, setOffset] = useState(0);
    const [more, setMore] = useState(false);
    const [moreLoading, setMoreLoading] = useState(false);

    const fetchPage = useCallback(
        async (fromOffset) => {
            const body = new URLSearchParams();
            body.append(
                "data",
                JSON.stringify({ category_id: apiRoutesUrls.articlesCategoryPolishMusicSources })
            );
            const res = await fetch(
                `${apiRoutesUrls.main}articles/search_articles/${fromOffset}/${PAGE_SIZE}?lang=${lang}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: body.toString(),
                }
            );
            if (!res.ok) {
                throw new Error(String(res.status));
            }
            return res.json();
        },
        [lang]
    );

    useEffect(() => {
        setData(null);
        setOffset(0);
        fetchPage(0)
            .then((json) => {
                setData(Array.isArray(json) ? json : []);
                setMore(Array.isArray(json) && json.length === PAGE_SIZE);
            })
            .catch(() => {
                setData([]);
                setMore(false);
            });
    }, [lang, fetchPage]);

    const handleShowMore = () => {
        if (!more || moreLoading) {
            return;
        }
        setMoreLoading(true);
        const next = offset + PAGE_SIZE;
        fetchPage(next)
            .then((json) => {
                const chunk = Array.isArray(json) ? json : [];
                setData((prev) => [...(prev || []), ...chunk]);
                setOffset(next);
                setMore(chunk.length === PAGE_SIZE);
            })
            .catch(() => setMore(false))
            .finally(() => setMoreLoading(false));
    };

    return (
        <>
            <Helmet>
                <title>
                    {translation.helmet.articles} - {translation.helmet.main}
                </title>
            </Helmet>
            <LoaderPage data={data} scroll={false} />
            {data ? (
                <div className={listStyles.wrapper}>
                    <div className={listStyles.backgroundTitle}>
                        <h1 className={listStyles.mainTitle}>{translation.titles.articles}</h1>
                    </div>
                    <div className={listStyles.containerList}>
                        <div className={listStyles.container}>
                            {data.map((item) => {
                                const imgUrl = apiRoutesUrls.articlesArticleImageUrl(item.id, item.image);
                                const detailPath = `/${lang}/artykuly/${parseInt(item.id, 10)}-${toUrlSlug(item.title)}/szczegoly`;
                                const cardTitle = item.title;
                                const authorsLine = formatAuthors(item.authors);
                                return (
                                    <Link
                                        key={item.id}
                                        className={listStyles.box}
                                        to={detailPath}
                                        title={cardTitle}
                                        aria-label={cardTitle}
                                    >
                                        <div
                                            className={listStyles.img}
                                            style={
                                                imgUrl
                                                    ? {
                                                          backgroundImage: `url("${encodeURI(imgUrl)}")`,
                                                      }
                                                    : undefined
                                            }
                                            role="presentation"
                                        />
                                        <div className={listStyles.item}>
                                            <div className={listStyles.set}>{item.title}</div>
                                            <div className={listStyles.title}>
                                                {translation.articles.authorLine}:{" "}
                                                {authorsLine || "—"}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                        <Loader loading={moreLoading} />
                    </div>
                    {more ? (
                        <button
                            type="button"
                            className={listStyles.more}
                            onClick={handleShowMore}
                            disabled={moreLoading}
                            title={translation.other.showMore}
                        >
                            {translation.other.showMore}
                        </button>
                    ) : null}
                    <Footer />
                </div>
            ) : null}
        </>
    );
};

export default Articles;
